<script lang="ts">
	import { Button, ButtonSize, ButtonType, DetailedSelect, Modal } from '@repo/commons/components'
	import type { DetailedSelectItem, FormValidationItem, ReferenceTable } from '@repo/commons/models'
	import { validateEmptyString, validateForm } from '@repo/commons/utils'
	import { t } from 'svelte-i18n'

	type Props = {
		visible: boolean
		referenceTables: readonly ReferenceTable[]
		configuredTableIds: readonly string[]
		onAdd: (table: ReferenceTable) => void
		onClose: VoidFunction
	}

	const { visible, referenceTables, configuredTableIds, onAdd, onClose }: Props = $props()

	let selectedTableId = $state<string | null>(null)
	let isTableTouched = $state(false)

	const tableItems: DetailedSelectItem[] = $derived(
		referenceTables.flatMap(({ id, name }) =>
			id ? [{ id, title: name ?? id, disabled: isAlreadyConfigured(id) }] : []
		)
	)

	const selectedTableItem = $derived(tableItems.find(({ id }) => id === selectedTableId) ?? null)

	const validationSchema: Record<'referenceTableId', FormValidationItem> = $derived({
		referenceTableId: { formComponent: selectedTableId ?? '', validators: [validateEmptyString] }
	})

	const errors = $derived(
		validateForm(validationSchema, { referenceTableId: isTableTouched }, false)
	)

	const isAlreadyConfigured = (tableId: string): boolean => configuredTableIds.includes(tableId)

	const handleSelect = (item: DetailedSelectItem | null): void => {
		selectedTableId = item?.id ?? null
		isTableTouched = true
	}

	const handleClose = (): void => {
		selectedTableId = null
		isTableTouched = false
		onClose()
	}

	const handleAdd = (): void => {
		isTableTouched = true

		const table = referenceTables.find(({ id }) => id === selectedTableId)
		if (!table) return

		onAdd(table)
		handleClose()
	}
</script>

<Modal
	title={$t('InstanceReferenceTables.configure')}
	{visible}
	onclose={handleClose}
	showCloseButton={false}
	closeOnBackdropClick={false}
>
	<div class="modal-content">
		<DetailedSelect
			label={$t('InstanceReferenceTables.selectTable')}
			items={tableItems}
			selectedItem={selectedTableItem}
			error={errors.referenceTableId}
			onSelect={handleSelect}
		/>

		<div class="modal-footer">
			<Button
				buttonText={$t('InstanceReferenceTables.add')}
				buttonType={ButtonType.BRAND}
				buttonSize={ButtonSize.LARGE}
				onclick={handleAdd}
			/>
			<Button
				buttonText={$t('FORM.cancel')}
				buttonType={ButtonType.TERTIARY_INVERSE}
				buttonSize={ButtonSize.LARGE}
				onclick={handleClose}
			/>
		</div>
	</div>
</Modal>

<style lang="postcss">
	.modal-content {
		@apply flex flex-col gap-4 w-[434px] max-w-full;
	}

	.modal-footer {
		@apply flex gap-4 mt-4;
	}
</style>
